import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Chrome, Lock, ArrowLeftRight, Sparkles, Tag, ShieldCheck } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { SOFTPHONE_FEATURES, SOFTPHONE_TABS } from '../data/mock';

const WEBPHONE_FAQ = [
  { q: 'What does "WebRTC" mean for the end user?', a: "It means zero installation. Open the unique URL we give you in Chrome, Edge or Safari, grant microphone access once, and you can make and receive calls. No plugin, no Java, no client to maintain." },
  { q: "Is there an app to download?", a: "Not for the Web Phone — that's the point. We also ship native iOS, Android, Windows and macOS apps if you prefer, but they're optional. Many customers run their whole team on the browser." },
  { q: "Can I log in with Google or Microsoft 365?", a: "Yes. Single Sign-On with Google Workspace and Microsoft 365 / Entra ID is supported out of the box — your users don't have to remember another password." },
  { q: "Can it be white-labelled?", a: "Yes. The Web App is brandable — your logo, colours and domain name. Customers see your brand, not ours." },
  { q: "What browsers are supported?", a: "All modern Chromium-based browsers (Chrome, Edge, Opera, Brave), Firefox, and Safari. We target the latest two major releases of each." },
  { q: "Is the audio encrypted?", a: "Yes. WebRTC signalling runs over TLS and media uses SRTP — every call is encrypted end-to-end between the browser and our SBC. No clear-text RTP on the wire." }
];

export default function WebPhone() {
  const [active, setActive] = useState(SOFTPHONE_TABS[0].key);
  const current = SOFTPHONE_TABS.find((t) => t.key === active);

  return (
    <>
      <Seo
        title="Web Phone & Softphone — WebRTC browser-based | VoIPinger"
        description="Browser-based WebRTC softphone with no install. Google + Microsoft 365 SSO, encrypted media (SRTP), brandable, switch live calls between devices in under a second."
        path="/web-phone"
        image="https://voipinger.com/assets/heroes/web-phone.png"
        service={{ name: 'Web Phone & Softphone', serviceType: 'WebRTC softphone / browser phone' }}
        faq={WEBPHONE_FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Web Phone', path: '/web-phone' }]}
      />
      <PageHero
        eyebrow="Web Phone | Browser Phone | Mobile App"
        title="Web Phone & Softphone"
        subtitle="Combine chat, voice and video calls in one user-friendly interface. macOS, Linux, Windows, iOS, Android and any modern browser — we’ve got you covered."
        breadcrumb="Web Phone"
      />

      {/* Highlights */}
      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">All-in-one Communicator</span>
            <h2 className="section-title mt-4">Our most advanced & feature-rich softphone</h2>
            <p className="section-sub mt-4">
              Make and receive calls as if it were a physical desk phone. Voice and video, messaging,
              presence, phone directory and fax-to-email — all in one polished interface.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {['Cheap VoIP calls', 'Add a new line now', 'Unified solution', 'Free your desk space', 'Video & messaging', 'Cross-platform'].map((b) => (
                <li key={b} className="flex items-center gap-2 text-slate-700">
                  <Check size={16} className="text-violet-600" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary">
                Try Free for 14 days <ArrowRight size={16} />
              </Link>
              <Link to="/business-voip" className="btn-light">View pricing</Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-violet-100 via-fuchsia-50 to-white rounded-3xl -z-10" />
            <img src={SOFTPHONE_FEATURES.popular} alt="Softphone with all popular features" className="w-full max-w-lg mx-auto" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Tabs of features */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Capabilities</span>
            <h2 className="section-title mt-4">A full communications platform</h2>
            <p className="section-sub mt-3">Telephone, fax, instant messenger, video, conferencing, collaboration and CRM — in one app.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {SOFTPHONE_TABS.map((t) => {
              const Ic = Icons[t.icon] || Icons.Phone;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-violet-300'
                  }`}
                >
                  <Ic size={16} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Wider card + 4/8 split favouring the media column so videos
              and mockups read at a usable size. Square AI "icon" mockups
              like Address Book + Productivity render with `compact:true`
              so they cap at ~22rem and don't dominate the row. */}
          <div className="max-w-7xl mx-auto bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-4">
              <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">{current.title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">{current.desc}</p>
              <ul className="space-y-2.5">
                {current.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-slate-700">
                    <Check size={16} className="text-violet-600 mt-1" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-8 relative flex items-center justify-center">
              <div className="absolute -inset-3 bg-gradient-to-br from-violet-100 to-fuchsia-50 rounded-2xl -z-10" />
              {/* Tab-specific media: video for the live demos, image for
                  the static mockups (conferencing grid + voicemail inbox).
                  `media.compact` caps the width for square icon-style
                  mockups so they look like badges instead of posters.
                  Falls back to the legacy SVG illustrations for any tab
                  that doesn't define a `media` block. */}
              {current.media?.type === 'video' ? (
                <video
                  key={current.key}
                  src={current.media.url}
                  className="w-full h-auto rounded-2xl shadow-xl"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={current.title}
                />
              ) : current.media?.type === 'image' ? (
                <img
                  src={current.media.url}
                  alt={current.title}
                  className={`h-auto rounded-2xl shadow-xl ${current.media.compact ? 'w-full max-w-sm' : 'w-full'}`}
                  loading="lazy"
                />
              ) : (
                <img
                  src={current.key === 'messaging' ? SOFTPHONE_FEATURES.messaging : current.key === 'softphone' ? SOFTPHONE_FEATURES.popular : SOFTPHONE_FEATURES.email}
                  alt={current.title}
                  className="w-full max-w-md mx-auto"
                  loading="lazy"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Platform support */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Available everywhere</span>
            <h2 className="section-title mt-4">Run on every desktop & mobile OS</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { t: 'Microsoft Windows', d: 'Windows 7 SP1, 8, 8.1, 10, 11', i: 'Monitor' },
              { t: 'Apple macOS', d: '10.10 Yosemite and above', i: 'AppWindow' },
              { t: 'Linux & Web', d: 'Ubuntu 16.04+ and modern browsers', i: 'Globe' }
            ].map((p) => {
              const Ic = Icons[p.i] || Icons.Monitor;
              return (
                <div key={p.t} className="card-modern text-center">
                  <div className="w-14 h-14 rounded-2xl bg-violet-50 text-violet-600 mx-auto mb-4 flex items-center justify-center">
                    <Ic size={24} />
                  </div>
                  <h3 className="font-bold mb-1">{p.t}</h3>
                  <p className="text-sm text-slate-500">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* WHY THE WEB APP — WebRTC + SSO + brandable                        */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> Built on WebRTC</span>
            <h2 className="section-title mt-4">The simplest way to give a team a phone system</h2>
            <p className="section-sub mt-3">
              No software install, no plugin, no admin permissions to fight with. Open a tab, sign in
              once, take calls. It scales from a one-person consultancy to a 500-seat contact centre on
              the same architecture.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              { i: Chrome, t: 'Zero install', d: 'WebRTC under the hood — opens straight in Chrome, Edge, Safari or Firefox. No plugin, no client to deploy.' },
              { i: Lock, t: 'SSO ready', d: 'Sign in with Google Workspace or Microsoft 365 / Entra ID. SCIM provisioning available for larger deployments.' },
              { i: Tag, t: 'Brandable', d: "Your logo, your colours, your domain. Customers see your brand — not ours." },
              { i: ShieldCheck, t: 'Encrypted by default', d: 'TLS signalling, SRTP media. No clear-text audio on the wire. Audit-logged session events.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                  <c.i size={22} />
                </div>
                <h3 className="font-extrabold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          {/* Handover callout */}
          <div className="max-w-4xl mx-auto mt-10 rounded-2xl border border-violet-100 bg-violet-50/50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                <ArrowLeftRight size={22} />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">Switch a live call to another device, mid-conversation</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  On a call in the Web App and need to leave the desk? Tap "Switch device" — the call
                  hops to your mobile in under a second, the caller hears nothing. The same trick from
                  mobile back to web, or to a SIP desk phone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion items={WEBPHONE_FAQ} title="Web Phone & Softphone FAQ" />

      <CtaBanner title="Try the Web Phone today" subtitle="Open a tab. Sign in with Google or Microsoft 365. Make your first call. That's the setup." />
    </>
  );
}
